import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PageTitleService {
  private readonly DEFAULT_TITLE = 'Maptycs';
  private readonly TITLE_SEPARATOR = ' - ';

  constructor(
    private title: Title,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  /**
   * Initialize page title tracking
   * Call this in AppComponent.ngOnInit()
   */
  init(): void {
    // Set initial title
    this.updateTitle();

    // Update title on route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute)
      )
      .subscribe(() => {
        this.updateTitle();
      });
  }

  /**
   * Set page title manually
   * @param title - Page title (e.g., "Datapoints - Locations")
   */
  setTitle(title: string): void {
    const fullTitle = title ? `${title}${this.TITLE_SEPARATOR}${this.DEFAULT_TITLE}` : this.DEFAULT_TITLE;
    this.title.setTitle(fullTitle);
  }

  /**
   * Update title based on current route
   */
  private updateTitle(): void {
    const url = this.router.url;
    const title = this.getTitleFromUrl(url);
    this.setTitle(title);
  }

  /**
   * Get meaningful title from URL
   */
  private getTitleFromUrl(url: string): string {
    // Remove query params and fragments
    const cleanUrl = url.split('?')[0].split('#')[0];
    
    // Map URLs to titles
    if (cleanUrl.includes('/datapoints/map')) {
      return 'Datapoints (Map)';
    } else if (cleanUrl.includes('/datapoints/table')) {
      return 'Datapoints (Table)';
    } else if (cleanUrl.includes('/dashboard')) {
      return 'Dashboard';
    } else if (cleanUrl.includes('/drafts')) {
      return 'Drafts';
    } else if (cleanUrl.includes('/global-overlays')) {
      return 'Global Overlays';
    } else if (cleanUrl.includes('/external-overlays')) {
      return 'External Overlays';
    } else if (cleanUrl.includes('/overlays/api-meter')) {
      return 'Api Metering';
    } else if (cleanUrl.includes('/account')) {
      if (cleanUrl.includes('/members')) {
        return 'Account Members';
      } else if (cleanUrl.includes('/overlays')) {
        return 'Account Overlays';
      }
      return 'Account';
    } else if (cleanUrl.includes('/integration')) {
      return 'Integration';
    } else if (cleanUrl.includes('/login')) {
      return 'Login';
    } else if (cleanUrl.includes('/home')) {
      return 'Home';
    }
    
    // Default to base title
    return '';
  }

  /**
   * Get title with dataset name
   * @param datasetName - Dataset name (e.g., "Locations")
   */
  getDatasetTitle(datasetName: string): string {
    return `Datapoints${datasetName ? ` - ${datasetName}` : ''}`;
  }

  /**
   * Set hierarchical page title with navigation path
   * @param basePath - Base path (e.g., "Datapoints (Map)")
   * @param subPath - Sub path (e.g., "Analytics Panel")
   * @param datasetName - Dataset name (optional)
   * 
   * @example
   * setHierarchicalTitle('Datapoints (Map)', 'Analytics Panel', 'Locations')
   * // Result: "Datapoints (Map) > Analytics Panel - Locations - Maptycs"
   */
  setHierarchicalTitle(basePath: string, subPath: string, datasetName?: string): void {
    const hierarchicalPath = `${basePath} > ${subPath}`;
    const fullTitle = datasetName 
      ? `${hierarchicalPath} - ${datasetName}`
      : hierarchicalPath;
    this.setTitle(fullTitle);
  }

  /**
   * Get view mode title
   * @param viewMode - 'map', 'table', or 'dashboard'
   * @param datasetName - Dataset name
   */
  getViewModeTitle(viewMode: 'map' | 'table' | 'dashboard', datasetName: string): string {
    const viewModeMap = {
      'map': 'Datapoints (Map)',
      'table': 'Datapoints (Table)',
      'dashboard': 'Dashboard'
    };
    return `${viewModeMap[viewMode]} - ${datasetName}`;
  }
}
